import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  AcademicCapIcon,
  BookOpenIcon,
  VideoCameraIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowLeftIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

const StudyProgramPage = () => {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingTask, setUpdatingTask] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProgram, setDeletingProgram] = useState(false);
  const [regeneratingProgram, setRegeneratingProgram] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const dayNames = {
    monday: 'Pazartesi',
    tuesday: 'Salı',
    wednesday: 'Çarşamba',
    thursday: 'Perşembe',
    friday: 'Cuma',
    saturday: 'Cumartesi',
    sunday: 'Pazar'
  };

  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => {
    fetchActiveProgram();
  }, [searchParams]);

  const fetchActiveProgram = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/study-programs/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Get program type from URL query parameter (TYT or AYT)
        const programType = searchParams.get('type');
        
        // Select the appropriate program based on type
        let selectedProgram = null;
        if (programType === 'TYT' && response.data.data.tyt) {
          selectedProgram = response.data.data.tyt;
        } else if (programType === 'AYT' && response.data.data.ayt) {
          selectedProgram = response.data.data.ayt;
        } else {
          // Default: use activeProgram (first available)
          selectedProgram = response.data.data.activeProgram;
        }

        if (selectedProgram) {
          setProgram(selectedProgram);
          setError('');
        } else {
          setError(`Aktif ${programType || ''} programı bulunamadı. Önce bir ${programType || ''} denemesi analizi yapıp program oluşturmalısın.`);
        }
      }
    } catch (err) {
      console.error('Program yükleme hatası:', err);
      if (err.response?.status === 404) {
        setError('Aktif program bulunamadı. Önce bir deneme analizi yapıp program oluşturmalısın.');
      } else {
        setError('Program yüklenirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskCompletion = async (taskId) => {
    try {
      setUpdatingTask(taskId);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/study-programs/${program.id}/tasks/${taskId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Update local state
        setProgram(prev => ({
          ...prev,
          completedTasks: response.data.data.program.completedTasks,
          completionPercentage: response.data.data.program.completionPercentage,
          tasks: prev.tasks.map(task =>
            task.id === taskId ? response.data.data.task : task
          )
        }));
      }
    } catch (err) {
      console.error('Görev güncelleme hatası:', err);
      alert('Görev güncellenirken bir hata oluştu');
    } finally {
      setUpdatingTask(null);
    }
  };

  const toggleSubTask = (taskId, subTaskIndex) => {
    setProgram(prev => ({
      ...prev,
      tasks: prev.tasks.map(task => {
        if (task.id !== taskId) return task;

        const newSubTasks = [...task.subTasks];
        newSubTasks[subTaskIndex] = {
          ...newSubTasks[subTaskIndex],
          completed: !newSubTasks[subTaskIndex].completed
        };

        // Check if all subtasks are completed
        const allCompleted = newSubTasks.every(st => st.completed);

        // If all subtasks completed, mark main task as completed
        if (allCompleted && !task.completed) {
          toggleTaskCompletion(taskId);
        }

        return {
          ...task,
          subTasks: newSubTasks
        };
      })
    }));
  };

  const handleMainTaskToggle = (task) => {
    // If marking as complete, complete all subtasks too
    if (!task.completed && task.subTasks && task.subTasks.length > 0) {
      setProgram(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => {
          if (t.id !== task.id) return t;
          return {
            ...t,
            subTasks: t.subTasks.map(st => ({ ...st, completed: true }))
          };
        })
      }));
    }
    toggleTaskCompletion(task.id);
  };

  const groupTasksByDay = () => {
    if (!program?.tasks) return {};

    const grouped = {};
    dayOrder.forEach(day => {
      grouped[day] = program.tasks.filter(task => task.day === day);
    });
    return grouped;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high':
        return 'Yüksek Öncelik';
      case 'medium':
        return 'Orta Öncelik';
      case 'low':
        return 'Düşük Öncelik';
      default:
        return priority;
    }
  };

  const searchYouTube = (query) => {
    const searchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
    window.open(searchUrl, '_blank');
  };

  const handleDeleteProgram = async () => {
    try {
      setDeletingProgram(true);
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/study-programs/${program.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Ders programı başarıyla silindi!');
      navigate('/dashboard');
    } catch (err) {
      console.error('Program silme hatası:', err);
      alert('Program silinirken bir hata oluştu!');
    } finally {
      setDeletingProgram(false);
      setShowDeleteModal(false);
    }
  };

  const handleRegenerateProgram = async () => {
    if (!window.confirm('Programı yeniden oluşturmak istediğinizden emin misiniz? Bu işlem 30-60 saniye sürebilir.')) {
      return;
    }

    try {
      setRegeneratingProgram(true);
      const token = localStorage.getItem('token');

      // First delete the existing program
      await axios.delete(
        `${API_URL}/study-programs/${program.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Then create a new one with the same exam (with longer timeout)
      const response = await axios.post(
        `${API_URL}/study-programs/create`,
        { examId: program.examId },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000 // 2 minutes timeout
        }
      );

      if (response.data.success) {
        alert('Ders programı başarıyla yeniden oluşturuldu!');
        window.location.reload();
      }
    } catch (err) {
      console.error('Program yeniden oluşturma hatası:', err);
      if (err.response?.status === 400) {
        alert(err.response.data.message || 'Program oluşturulurken bir hata oluştu!');
      } else if (err.code === 'ECONNABORTED') {
        alert('İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.');
      } else {
        alert('Program yeniden oluşturulurken bir hata oluştu! Lütfen birkaç saniye bekleyip tekrar deneyin.');
      }
    } finally {
      setRegeneratingProgram(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Program yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Program Bulunamadı</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Dashboard'a Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tasksByDay = groupTasksByDay();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Dashboard'a Dön
          </button>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Haftalık Çalışma Programın
                </h1>
                <p className="text-gray-600">
                  {program?.exam?.examName} • Hafta {program?.weekNumber}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleRegenerateProgram}
                  disabled={regeneratingProgram}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Programı yeniden oluştur"
                >
                  {regeneratingProgram ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition shadow-md hover:shadow-lg"
                  title="Programı sil"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
                <CalendarIcon className="h-12 w-12 text-blue-600" />
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">İlerleme</span>
                <span className="text-sm font-medium text-gray-900">
                  {program?.completedTasks || 0} / {program?.totalTasks || 0} Görev
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-blue-600 to-purple-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${program?.completionPercentage || 0}%` }}
                ></div>
              </div>
              <p className="text-right mt-1 text-sm text-gray-600">
                %{Math.round(program?.completionPercentage || 0)} Tamamlandı
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center">
                  <ChartBarIcon className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Toplam Görev</p>
                    <p className="text-2xl font-bold text-gray-900">{program?.totalTasks || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircleSolid className="h-8 w-8 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Tamamlanan</p>
                    <p className="text-2xl font-bold text-gray-900">{program?.completedTasks || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center">
                  <ClockIcon className="h-8 w-8 text-purple-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Kalan Görev</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {(program?.totalTasks || 0) - (program?.completedTasks || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Tasks */}
        <div className="space-y-6">
          {dayOrder.map(day => {
            const tasks = tasksByDay[day] || [];
            if (tasks.length === 0) return null;

            const completedCount = tasks.filter(t => t.completed).length;

            return (
              <div key={day} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">{dayNames[day]}</h2>
                  <span className="text-sm text-gray-600">
                    {completedCount} / {tasks.length} Tamamlandı
                  </span>
                </div>

                <div className="space-y-4">
                  {tasks.map(task => (
                    <div
                      key={task.id}
                      className={`border-2 rounded-lg p-5 transition-all ${
                        task.completed
                          ? 'bg-green-50 border-green-200'
                          : 'bg-white border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-start">
                        {/* Checkbox */}
                        <button
                          onClick={() => handleMainTaskToggle(task)}
                          disabled={updatingTask === task.id}
                          className="mt-1 mr-4 flex-shrink-0 focus:outline-none"
                        >
                          {task.completed ? (
                            <CheckCircleSolid className="h-7 w-7 text-green-600" />
                          ) : (
                            <div className="h-7 w-7 rounded-full border-2 border-gray-300 hover:border-blue-600 transition-colors"></div>
                          )}
                        </button>

                        {/* Task Content */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className={`text-lg font-semibold ${task.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                {task.subject} - {task.topic}
                              </h3>
                              <span className={`inline-block mt-1 px-3 py-1 text-xs font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
                                {getPriorityLabel(task.priority)}
                              </span>
                            </div>
                          </div>

                          <p className={`text-gray-700 mb-3 ${task.completed ? 'line-through' : ''}`}>
                            {task.description}
                          </p>

                          {/* Sub-Tasks with Checkboxes */}
                          {task.subTasks && task.subTasks.length > 0 && (
                            <div className="mb-3 ml-6 space-y-2 border-l-2 border-gray-200 pl-4">
                              {task.subTasks.map((subTask, idx) => (
                                <div key={idx} className="flex items-start">
                                  <button
                                    onClick={() => toggleSubTask(task.id, idx)}
                                    className="mt-0.5 mr-3 flex-shrink-0 focus:outline-none"
                                  >
                                    {subTask.completed ? (
                                      <CheckCircleSolid className="h-5 w-5 text-green-600" />
                                    ) : (
                                      <div className="h-5 w-5 rounded-full border-2 border-gray-300 hover:border-blue-500 transition-colors"></div>
                                    )}
                                  </button>
                                  <span className={`text-sm ${subTask.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                    {subTask.text}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Task Meta */}
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <div className="flex items-center">
                              <ClockIcon className="h-4 w-4 mr-1" />
                              {task.duration} dakika
                            </div>
                            <div className="flex items-center">
                              <BookOpenIcon className="h-4 w-4 mr-1" />
                              {task.questionTarget} soru hedefi
                            </div>
                            {task.searchQuery && (
                              <button
                                onClick={() => searchYouTube(task.searchQuery)}
                                className="flex items-center text-red-600 hover:text-red-700 font-medium"
                              >
                                <VideoCameraIcon className="h-4 w-4 mr-1" />
                                Video İzle
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex gap-4 justify-center">
          <button
            onClick={() => navigate('/study-program/history')}
            className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Geçmiş Programlar
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <TrashIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
              Ders Programını Sil
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Bu ders programını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              Tüm görevler ve ilerleme kaydedilmez.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deletingProgram}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteProgram}
                disabled={deletingProgram}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {deletingProgram ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Loading Overlay */}
      {(regeneratingProgram || deletingProgram) && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md text-center shadow-2xl">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {regeneratingProgram ? 'Program Yeniden Oluşturuluyor...' : 'Program Siliniyor...'}
            </h3>
            <p className="text-gray-600 mb-4">
              {regeneratingProgram ? 'Bu işlem 30-60 saniye sürebilir. Lütfen bekleyin ve sayfayı kapatmayın.' : 'Lütfen bekleyin...'}
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <div className="animate-pulse">●</div>
              <div className="animate-pulse animation-delay-200">●</div>
              <div className="animate-pulse animation-delay-400">●</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyProgramPage;
